/**
 * PlatformSettingsView — Platform-level settings panel.
 *
 * Tabs:
 *  - Tenants: list, create, suspend/activate
 *  - Organizations: list (filterable by tenant), create, module toggles
 *  - AI: platform Anthropic API key
 *  - Demo: demo org management (seed / list / delete)
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useUIStore } from '../../../stores/uiStore';
import { getTranslation } from '../../../i18n';
import Globe from 'lucide-react/dist/esm/icons/globe';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Users from 'lucide-react/dist/esm/icons/users';
import Copy from 'lucide-react/dist/esm/icons/copy';
import Check from 'lucide-react/dist/esm/icons/check';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import UserPlus from 'lucide-react/dist/esm/icons/user-plus';
import { Button, IconButton, Spinner, Badge, Text, TextField, Modal } from '../../../components/ui';
import { Select } from '../../../components/ui';
import { useToast } from '../../../components/feedback';
import ModuleBadges from '../../../components/ui/ModuleBadges';
import InviteUserModal from '../../org-management/components/InviteUserModal';
import {
  listTenants, createTenant, updateTenant,
  listTenantOrganizations, listAllOrganizations, createOrganization,
  listOrgModules, toggleOrgModule, getModuleRegistry,
  listPlatformOrgUsers, listPlatformOrgDepartments, platformInviteUser,
  seedDemoOrg, listDemoOrgs, deleteDemoOrg,
  getPlatformLlmApiKey, setPlatformLlmApiKey, clearPlatformLlmApiKey,
  getPlatformLlmModel, setPlatformLlmModel,
} from '../../../services/api/platform';
import type {
  Tenant, PlatformOrganization, OrgModuleAccess,
  DemoSeedResponse, DemoOrgListItem, LlmApiKeyStatus,
} from '../../../services/api/platform';
import type { ModuleRegistryItem, OrgUser, Department, LlmApiKeyStatus as OrgLlmApiKeyStatus } from '../../../services/api/org';
import {
  getSalesPublicApiKey,
  regenerateSalesPublicApiKey,
  getOrgLlmApiKey,
  setOrgLlmApiKey,
  clearOrgLlmApiKey,
} from '../../../services/api/org';
import SalesPublicApiKeyBlock from '../../org-management/components/SalesPublicApiKeyBlock';
import LlmApiKeyBlock from '../../org-management/components/LlmApiKeyBlock';

type PlatformTab = 'tenants' | 'organizations' | 'demo' | 'ai';

// ── Tenants Tab ──

const TenantsPanel: React.FC = () => {
  const { language } = useUIStore();
  const tr = (key: string, vars?: Record<string, string | number>) => getTranslation(language, key, vars);
  const { success, error } = useToast();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [creating, setCreating] = useState(false);
  // Per-tenant expanded state + lazily-loaded orgs/modules
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null);
  const [tenantOrgs, setTenantOrgs] = useState<Record<string, PlatformOrganization[]>>({});
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [orgModules, setOrgModules] = useState<OrgModuleAccess[]>([]);
  const [registry, setRegistry] = useState<ModuleRegistryItem[]>([]);
  const [provisionPrompt, setProvisionPrompt] = useState<{ orgId: string; moduleId: string } | null>(null);
  // Invite state
  const [inviteTarget, setInviteTarget] = useState<{ org: PlatformOrganization; enabledModules: string[]; users: OrgUser[]; departments: Department[] } | null>(null);
  const [inviteLoading, setInviteLoading] = useState<string | null>(null);
  // LLM API key per org (platform admin)
  const [orgLlmStatus, setOrgLlmStatus] = useState<Record<string, OrgLlmApiKeyStatus>>({});
  const [orgLlmLoading, setOrgLlmLoading] = useState<Record<string, boolean>>({});
  const [orgLlmError, setOrgLlmError] = useState<Record<string, boolean>>({});
  const [orgLlmSavingId, setOrgLlmSavingId] = useState<string | null>(null);
  const [orgLlmClearingId, setOrgLlmClearingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([listTenants(), getModuleRegistry()]);
      setTenants(t);
      setRegistry(r);
    } catch { /* empty */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadOrgLlmKey = async (orgId: string) => {
    setOrgLlmLoading((p) => ({ ...p, [orgId]: true }));
    setOrgLlmError((p) => ({ ...p, [orgId]: false }));
    try {
      const status = await getOrgLlmApiKey(orgId);
      setOrgLlmStatus((prev) => ({ ...prev, [orgId]: status }));
    } catch {
      setOrgLlmError((p) => ({ ...p, [orgId]: true }));
    } finally {
      setOrgLlmLoading((p) => ({ ...p, [orgId]: false }));
    }
  };

  const handleExpandTenant = async (tenantId: string) => {
    if (expandedTenant === tenantId) {
      setExpandedTenant(null);
      setExpandedOrg(null);
      return;
    }
    setExpandedTenant(tenantId);
    setExpandedOrg(null);
    if (!tenantOrgs[tenantId]) {
      try {
        const orgs = await listTenantOrganizations(tenantId);
        setTenantOrgs((prev) => ({ ...prev, [tenantId]: orgs }));
      } catch { /* empty */ }
    }
  };

  const handleExpandOrg = async (orgId: string) => {
    if (expandedOrg === orgId) { setExpandedOrg(null); return; }
    setExpandedOrg(orgId);
    try {
      setOrgModules(await listOrgModules(orgId));
    } catch { /* empty */ }
    void loadOrgLlmKey(orgId);
  };

  const handleSaveOrgLlmKey = async (orgId: string, apiKey: string) => {
    setOrgLlmSavingId(orgId);
    try {
      const status = await setOrgLlmApiKey(apiKey, orgId);
      setOrgLlmStatus((prev) => ({ ...prev, [orgId]: status }));
      setOrgLlmError((p) => ({ ...p, [orgId]: false }));
      success(tr('settingsLlmSaveSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmSaveError');
      error(String(msg));
      throw e;
    } finally {
      setOrgLlmSavingId(null);
    }
  };

  const handleClearOrgLlmKey = async (orgId: string) => {
    setOrgLlmClearingId(orgId);
    try {
      const status = await clearOrgLlmApiKey(orgId);
      setOrgLlmStatus((prev) => ({ ...prev, [orgId]: status }));
      setOrgLlmError((p) => ({ ...p, [orgId]: false }));
      success(tr('settingsLlmClearSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmClearError');
      error(String(msg));
      throw e;
    } finally {
      setOrgLlmClearingId(null);
    }
  };

  const handleToggleModule = async (orgId: string, moduleId: string, currentEnabled: boolean) => {
    if (!currentEnabled) {
      setProvisionPrompt({ orgId, moduleId });
      return;
    }
    try {
      await toggleOrgModule(orgId, moduleId, false);
      success(tr('settingsTenantsToastModuleDisabled'));
      setOrgModules(await listOrgModules(orgId));
    } catch (e: any) {
      error(e?.response?.data?.detail || tr('settingsTenantsErrorFailedToToggleModule'));
    }
  };

  const handleProvisionConfirm = async (provision: 'all' | 'none') => {
    if (!provisionPrompt) return;
    try {
      await toggleOrgModule(provisionPrompt.orgId, provisionPrompt.moduleId, true, provision);
      success(tr('settingsTenantsToastModuleEnabled'));
      setOrgModules(await listOrgModules(provisionPrompt.orgId));
    } catch (e: any) {
      error(e?.response?.data?.detail || tr('settingsTenantsErrorFailedToEnableModule'));
    }
    setProvisionPrompt(null);
  };

  const handleInviteClick = async (org: PlatformOrganization) => {
    setInviteLoading(org.id);
    try {
      const [modulesResult, usersResult, deptsResult] = await Promise.allSettled([
        listOrgModules(org.id),
        listPlatformOrgUsers(org.id),
        listPlatformOrgDepartments(org.id),
      ]);
      const modules = modulesResult.status === 'fulfilled' ? modulesResult.value : [];
      const users = usersResult.status === 'fulfilled' ? usersResult.value : [];
      const departments = deptsResult.status === 'fulfilled' ? deptsResult.value : [];
      const enabledModules = modules.filter((m) => m.enabled).map((m) => m.module_id);
      setInviteTarget({ org, enabledModules, users, departments });
    } catch { /* empty */ } finally {
      setInviteLoading(null);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newSlug.trim()) return;
    setCreating(true);
    try {
      await createTenant({ name: newName.trim(), slug: newSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-') });
      success(tr('settingsTenantsToastTenantCreated'));
      setNewName(''); setNewSlug(''); setShowCreate(false);
      load();
    } catch (e: any) {
      error(e?.response?.data?.detail || tr('settingsTenantsErrorFailedToCreateTenant'));
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (e: React.MouseEvent, t: Tenant) => {
    e.stopPropagation();
    const newStatus = t.status === 'active' ? 'suspended' : 'active';
    try {
      await updateTenant(t.id, { status: newStatus });
      success(newStatus === 'suspended' ? tr('settingsTenantsToastTenantSuspended') : tr('settingsTenantsToastTenantActivated'));
      load();
    } catch (e: any) {
      error((e as any)?.response?.data?.detail || tr('settingsTenantsErrorFailedToUpdateTenant'));
    }
  };

  const isModuleEnabled = (moduleId: string) =>
    orgModules.some((m) => m.module_id === moduleId && m.enabled);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Spinner /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Text variant="subheading">{tenants.length !== 1 ? tr('settingsTenantsCountPlural', { count: tenants.length }) : tr('settingsTenantsCount', { count: tenants.length })}</Text>
        <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setShowCreate(true)}>
          {tr('settingsTenantsBtnCreateTenant')}
        </Button>
      </div>

      {showCreate && (
        <div className="p-4 rounded-xl bg-surface-primary border border-subtle space-y-3">
          <TextField label={tr('settingsTenantsFieldName')} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={tr('settingsTenantsPlaceholderName')} />
          <TextField label={tr('settingsTenantsFieldSlug')} value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder={tr('settingsTenantsPlaceholderSlug')} />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>{tr('settingsTenantsBtnCancel')}</Button>
            <Button variant="primary" size="sm" loading={creating} disabled={!newName.trim() || !newSlug.trim()} onClick={handleCreate}>{tr('settingsTenantsBtnCreate')}</Button>
          </div>
        </div>
      )}

      {tenants.length === 0 ? (
        <div className="text-center py-8">
          <Building2 size={24} className="text-content-faint mx-auto mb-2" />
          <Text variant="body" className="text-content-muted">{tr('settingsTenantsNoTenants')}</Text>
        </div>
      ) : (
        <div className="space-y-2">
          {tenants.map((t) => {
            const isExpanded = expandedTenant === t.id;
            const orgs = tenantOrgs[t.id] || [];
            return (
              <div key={t.id} className="rounded-xl bg-surface-primary border border-subtle overflow-hidden">
                {/* Tenant row */}
                <button
                  onClick={() => handleExpandTenant(t.id)}
                  className="w-full flex items-center gap-4 p-4 text-left border-none outline-none cursor-pointer bg-transparent hover:bg-surface-elevated/50 transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                    <Building2 size={18} className="text-brand/60" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-content block">{t.name}</span>
                    <span className="text-[11px] text-content-muted font-mono">{t.slug}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="flex items-center gap-1 text-content-muted">
                      <Building2 size={10} /> {t.org_count ?? 0}
                    </span>
                    <span className="flex items-center gap-1 text-content-muted">
                      <Users size={10} /> {t.user_count ?? 0}
                    </span>
                  </div>
                  <Badge variant={t.status === 'active' ? 'success' : 'danger'} size="sm">
                    {t.status}
                  </Badge>
                  <Button
                    variant={t.status === 'active' ? 'danger' : 'success'}
                    size="sm"
                    onClick={(e) => handleToggleStatus(e, t)}
                  >
                    {t.status === 'active' ? tr('settingsTenantsBtnSuspend') : tr('settingsTenantsBtnActivate')}
                  </Button>
                  {isExpanded
                    ? <ChevronUp size={14} className="text-content-muted shrink-0" />
                    : <ChevronDown size={14} className="text-content-muted shrink-0" />
                  }
                </button>

                {/* Orgs list */}
                {isExpanded && (
                  <div className="border-t border-subtle px-4 pb-3 pt-2 space-y-1.5 bg-surface-page/50">
                    {orgs.length === 0 ? (
                      <p className="text-xs text-content-muted py-2 pl-1">{tr('settingsTenantsNoOrgsInTenant')}</p>
                    ) : (
                      orgs.map((org) => {
                        const isOrgExpanded = expandedOrg === org.id;
                        return (
                          <div key={org.id} className="rounded-lg bg-surface-primary border border-subtle overflow-hidden">
                            {/* Org row */}
                            <div className="flex items-stretch">
                              <button
                                onClick={() => handleExpandOrg(org.id)}
                                className="flex-1 flex items-center gap-3 px-3 py-2.5 text-left border-none outline-none cursor-pointer bg-transparent hover:bg-surface-elevated/50 transition-all"
                              >
                                <div className="w-7 h-7 rounded-lg bg-status-success/10 flex items-center justify-center shrink-0">
                                  <Globe size={14} className="text-status-success/60" />
                                </div>
                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                  <span className="text-xs font-medium text-content">{org.name}</span>
                                  {org.id === t.primary_org_id && (
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-brand/10 text-brand border border-brand/20">{tr('settingsTenantsBadgeHq')}</span>
                                  )}
                                </div>
                                <span className="flex items-center gap-1 text-[11px] text-content-muted">
                                  <Users size={10} /> {org.user_count ?? 0}
                                </span>
                                <Badge variant={org.status === 'active' ? 'success' : 'danger'} size="sm">
                                  {org.status}
                                </Badge>
                                {isOrgExpanded
                                  ? <ChevronUp size={12} className="text-content-muted shrink-0" />
                                  : <ChevronDown size={12} className="text-content-muted shrink-0" />
                                }
                              </button>
                              <div className="flex items-center px-2 border-l border-subtle">
                                <IconButton
                                  icon={<UserPlus size={12} />}
                                  variant="ghost"
                                  size="sm"
                                  aria-label={tr('settingsTenantsAriaLabelInviteUser')}
                                  loading={inviteLoading === org.id}
                                  onClick={() => handleInviteClick(org)}
                                />
                              </div>
                            </div>

                            {/* Org details */}
                            {isOrgExpanded && (
                              <div className="border-t border-subtle px-3 pb-2 pt-2 space-y-3">
                                <LlmApiKeyBlock
                                  tr={tr}
                                  status={orgLlmStatus[org.id] ?? null}
                                  loading={!!orgLlmLoading[org.id]}
                                  error={!!orgLlmError[org.id]}
                                  saving={orgLlmSavingId === org.id}
                                  clearing={orgLlmClearingId === org.id}
                                  compact
                                  orgScope
                                  onSave={(key) => handleSaveOrgLlmKey(org.id, key)}
                                  onClear={() => handleClearOrgLlmKey(org.id)}
                                />
                                <div className="space-y-1.5">
                                  <Text variant="label" className="mb-1.5">{tr('settingsTenantsModuleAccess')}</Text>
                                  {registry.map((mod) => {
                                    const enabled = isModuleEnabled(mod.id);
                                    return (
                                      <div key={mod.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-surface-page border border-subtle">
                                        <div className="flex items-center gap-2">
                                          <ModuleBadges modules={[mod.id]} size="sm" />
                                          <span className="text-xs text-content">{mod.name}</span>
                                        </div>
                                        <button
                                          onClick={() => handleToggleModule(org.id, mod.id, enabled)}
                                          className={`relative w-9 h-5 rounded-full border-none cursor-pointer transition-colors shrink-0 ${enabled ? 'bg-status-success' : 'bg-surface-primary border border-default'}`}
                                        >
                                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${enabled ? 'left-4' : 'left-0.5'}`} />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Provision prompt modal */}
      {provisionPrompt && (
        <Modal isOpen onClose={() => setProvisionPrompt(null)}>
          <Modal.Header title={tr('settingsProvisionModalTitle')} icon={<AlertTriangle size={20} className="text-amber-400" />} onClose={() => setProvisionPrompt(null)} />
          <Modal.Body>
            <Text variant="body" className="text-content-secondary">
              {tr('settingsProvisionModalBody')}
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onClick={() => setProvisionPrompt(null)}>{tr('settingsProvisionBtnCancel')}</Button>
            <Button variant="secondary" onClick={() => handleProvisionConfirm('none')}>{tr('settingsProvisionBtnNoneManual')}</Button>
            <Button variant="primary" onClick={() => handleProvisionConfirm('all')}>{tr('settingsProvisionBtnAllUsers')}</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Invite user modal */}
      {inviteTarget && (
        <InviteUserModal
          isOpen
          onClose={() => setInviteTarget(null)}
          users={inviteTarget.users}
          departments={inviteTarget.departments}
          orgEnabledModules={inviteTarget.enabledModules}
          moduleRegistry={registry}
          onInvite={(data) => platformInviteUser(inviteTarget.org.id, data)}
        />
      )}
    </div>
  );
};

// ── Organizations Tab ──

const OrgsPanel: React.FC = () => {
  const { language } = useUIStore();
  const tr = (key: string, vars?: Record<string, string | number>) => getTranslation(language, key, vars);
  const { success, error } = useToast();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [orgs, setOrgs] = useState<PlatformOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTenant, setFilterTenant] = useState<string>('');
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [orgModules, setOrgModules] = useState<OrgModuleAccess[]>([]);
  const [registry, setRegistry] = useState<ModuleRegistryItem[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgTenant, setNewOrgTenant] = useState('');
  const [creating, setCreating] = useState(false);
  const [provisionPrompt, setProvisionPrompt] = useState<{ orgId: string; moduleId: string } | null>(null);
  // Invite state
  const [inviteTarget, setInviteTarget] = useState<{ org: PlatformOrganization; enabledModules: string[]; users: OrgUser[]; departments: Department[] } | null>(null);
  const [inviteLoading, setInviteLoading] = useState<string | null>(null);
  // API key per org
  const [orgApiKeys, setOrgApiKeys] = useState<Record<string, string>>({});
  const [orgApiKeyLoading, setOrgApiKeyLoading] = useState<Record<string, boolean>>({});
  const [orgApiKeyError, setOrgApiKeyError] = useState<Record<string, boolean>>({});
  const [regeneratingOrgId, setRegeneratingOrgId] = useState<string | null>(null);
  // LLM API key per org
  const [orgLlmStatus, setOrgLlmStatus] = useState<Record<string, OrgLlmApiKeyStatus>>({});
  const [orgLlmLoading, setOrgLlmLoading] = useState<Record<string, boolean>>({});
  const [orgLlmError, setOrgLlmError] = useState<Record<string, boolean>>({});
  const [orgLlmSavingId, setOrgLlmSavingId] = useState<string | null>(null);
  const [orgLlmClearingId, setOrgLlmClearingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, o, r] = await Promise.all([
        listTenants(), listAllOrganizations(filterTenant || undefined), getModuleRegistry(),
      ]);
      setTenants(t); setOrgs(o); setRegistry(r);
    } catch { /* empty */ } finally {
      setLoading(false);
    }
  }, [filterTenant]);

  useEffect(() => { load(); }, [load]);

  const loadOrgLlmKey = async (orgId: string) => {
    setOrgLlmLoading((p) => ({ ...p, [orgId]: true }));
    setOrgLlmError((p) => ({ ...p, [orgId]: false }));
    try {
      const status = await getOrgLlmApiKey(orgId);
      setOrgLlmStatus((prev) => ({ ...prev, [orgId]: status }));
    } catch {
      setOrgLlmError((p) => ({ ...p, [orgId]: true }));
    } finally {
      setOrgLlmLoading((p) => ({ ...p, [orgId]: false }));
    }
  };

  const handleExpandOrg = async (orgId: string) => {
    if (expandedOrg === orgId) { setExpandedOrg(null); return; }
    setExpandedOrg(orgId);
    try {
      setOrgModules(await listOrgModules(orgId));
    } catch { /* empty */ }
    if (!orgApiKeys[orgId]) {
      setOrgApiKeyLoading((p) => ({ ...p, [orgId]: true }));
      setOrgApiKeyError((p) => ({ ...p, [orgId]: false }));
      try {
        const { public_api_key } = await getSalesPublicApiKey(orgId);
        setOrgApiKeys((prev) => ({ ...prev, [orgId]: public_api_key }));
      } catch {
        setOrgApiKeyError((p) => ({ ...p, [orgId]: true }));
      } finally {
        setOrgApiKeyLoading((p) => ({ ...p, [orgId]: false }));
      }
    }
    void loadOrgLlmKey(orgId);
  };

  const handleRegenerateApiKey = async (orgId: string) => {
    setRegeneratingOrgId(orgId);
    try {
      const { public_api_key } = await regenerateSalesPublicApiKey(orgId);
      setOrgApiKeys((prev) => ({ ...prev, [orgId]: public_api_key }));
      setOrgApiKeyError((p) => ({ ...p, [orgId]: false }));
      success(tr('settingsOrgsLeadApiKeyRegenerateSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsOrgsLeadApiKeyRegenerateError');
      error(String(msg));
      throw e;
    } finally {
      setRegeneratingOrgId(null);
    }
  };

  const handleSaveOrgLlmKey = async (orgId: string, apiKey: string) => {
    setOrgLlmSavingId(orgId);
    try {
      const status = await setOrgLlmApiKey(apiKey, orgId);
      setOrgLlmStatus((prev) => ({ ...prev, [orgId]: status }));
      setOrgLlmError((p) => ({ ...p, [orgId]: false }));
      success(tr('settingsLlmSaveSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmSaveError');
      error(String(msg));
      throw e;
    } finally {
      setOrgLlmSavingId(null);
    }
  };

  const handleClearOrgLlmKey = async (orgId: string) => {
    setOrgLlmClearingId(orgId);
    try {
      const status = await clearOrgLlmApiKey(orgId);
      setOrgLlmStatus((prev) => ({ ...prev, [orgId]: status }));
      setOrgLlmError((p) => ({ ...p, [orgId]: false }));
      success(tr('settingsLlmClearSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmClearError');
      error(String(msg));
      throw e;
    } finally {
      setOrgLlmClearingId(null);
    }
  };

  const handleToggleModule = async (orgId: string, moduleId: string, currentEnabled: boolean) => {
    if (!currentEnabled) {
      setProvisionPrompt({ orgId, moduleId });
      return;
    }
    try {
      await toggleOrgModule(orgId, moduleId, false);
      success(tr('settingsTenantsToastModuleDisabled'));
      setOrgModules(await listOrgModules(orgId));
    } catch (e: any) {
      error(e?.response?.data?.detail || tr('settingsTenantsErrorFailedToToggleModule'));
    }
  };

  const handleProvisionConfirm = async (provision: 'all' | 'none') => {
    if (!provisionPrompt) return;
    try {
      await toggleOrgModule(provisionPrompt.orgId, provisionPrompt.moduleId, true, provision);
      success(tr('settingsTenantsToastModuleEnabled'));
      setOrgModules(await listOrgModules(provisionPrompt.orgId));
    } catch (e: any) {
      error(e?.response?.data?.detail || tr('settingsTenantsErrorFailedToEnableModule'));
    }
    setProvisionPrompt(null);
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim() || !newOrgTenant) return;
    setCreating(true);
    try {
      await createOrganization(newOrgTenant, { name: newOrgName.trim() });
      success(tr('settingsOrgsToastOrgCreated'));
      setNewOrgName(''); setShowCreate(false);
      load();
    } catch (e: any) {
      error(e?.response?.data?.detail || tr('settingsOrgsErrorFailedToCreateOrg'));
    } finally {
      setCreating(false);
    }
  };

  const handleInviteClick = async (org: PlatformOrganization) => {
    setInviteLoading(org.id);
    try {
      const [modulesResult, usersResult, deptsResult] = await Promise.allSettled([
        listOrgModules(org.id),
        listPlatformOrgUsers(org.id),
        listPlatformOrgDepartments(org.id),
      ]);
      const modules = modulesResult.status === 'fulfilled' ? modulesResult.value : [];
      const users = usersResult.status === 'fulfilled' ? usersResult.value : [];
      const departments = deptsResult.status === 'fulfilled' ? deptsResult.value : [];
      const enabledModules = modules.filter((m) => m.enabled).map((m) => m.module_id);
      setInviteTarget({ org, enabledModules, users, departments });
    } catch { /* empty */ } finally {
      setInviteLoading(null);
    }
  };

  const isModuleEnabled = (moduleId: string) =>
    orgModules.some((m) => m.module_id === moduleId && m.enabled);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Spinner /></div>;
  }

  const tenantName = (tenantId: string) => tenants.find((t) => t.id === tenantId)?.name || tr('settingsOrgsUnknownTenant');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <Select
            value={filterTenant}
            onChange={setFilterTenant}
            options={[
              { value: '', label: tr('settingsOrgsPlaceholderAllTenants') },
              ...tenants.map((t) => ({ value: t.id, label: t.name })),
            ]}
            placeholder={tr('settingsOrgsFilterByTenant')}
          />
        </div>
        <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setShowCreate(true)}>
          {tr('settingsOrgsBtnCreateOrg')}
        </Button>
      </div>

      {showCreate && (
        <div className="p-4 rounded-xl bg-surface-primary border border-subtle space-y-3">
          <Select
            value={newOrgTenant}
            onChange={setNewOrgTenant}
            options={tenants.map((t) => ({ value: t.id, label: t.name }))}
            placeholder={tr('settingsOrgsPlaceholderSelectTenant')}
          />
          <TextField label={tr('settingsOrgsFieldOrganizationName')} value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} placeholder={tr('settingsOrgsPlaceholderOrgName')} />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>{tr('settingsTenantsBtnCancel')}</Button>
            <Button variant="primary" size="sm" loading={creating} disabled={!newOrgName.trim() || !newOrgTenant} onClick={handleCreateOrg}>{tr('settingsTenantsBtnCreate')}</Button>
          </div>
        </div>
      )}

      <Text variant="label">{orgs.length !== 1 ? tr('settingsOrgsCountPlural', { count: orgs.length }) : tr('settingsOrgsCount', { count: orgs.length })}</Text>

      {orgs.length === 0 ? (
        <div className="text-center py-8">
          <Globe size={24} className="text-content-faint mx-auto mb-2" />
          <Text variant="body" className="text-content-muted">{tr('settingsOrgsNoOrganizations')}</Text>
        </div>
      ) : (
        <div className="space-y-2">
          {orgs.map((org) => {
            const isExpanded = expandedOrg === org.id;
            return (
              <div key={org.id} className="rounded-xl bg-surface-primary border border-subtle overflow-hidden">
                <div className="flex items-stretch">
                  <button
                    onClick={() => handleExpandOrg(org.id)}
                    className="flex-1 flex items-center gap-4 p-4 text-left border-none outline-none cursor-pointer bg-transparent hover:bg-surface-elevated/50 transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-status-success/10 flex items-center justify-center shrink-0">
                      <Globe size={18} className="text-status-success/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-content block">{org.name}</span>
                      <span className="text-[11px] text-content-muted">{tenantName(org.tenant_id)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={org.status === 'active' ? 'success' : 'danger'} size="sm">{org.status}</Badge>
                      <span className="flex items-center gap-1 text-[11px] text-content-muted">
                        <Users size={10} /> {org.user_count ?? 0}
                      </span>
                      {isExpanded ? <ChevronUp size={14} className="text-content-muted" /> : <ChevronDown size={14} className="text-content-muted" />}
                    </div>
                  </button>
                  <div className="flex items-center px-3 border-l border-subtle">
                    <IconButton
                      icon={<UserPlus size={14} />}
                      variant="ghost"
                      size="sm"
                      aria-label={tr('settingsOrgsAriaLabelInviteUser')}
                      loading={inviteLoading === org.id}
                      onClick={() => handleInviteClick(org)}
                    />
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 border-t border-subtle space-y-4">
                    <LlmApiKeyBlock
                      tr={tr}
                      status={orgLlmStatus[org.id] ?? null}
                      loading={!!orgLlmLoading[org.id]}
                      error={!!orgLlmError[org.id]}
                      saving={orgLlmSavingId === org.id}
                      clearing={orgLlmClearingId === org.id}
                      compact
                      orgScope
                      onSave={(key) => handleSaveOrgLlmKey(org.id, key)}
                      onClear={() => handleClearOrgLlmKey(org.id)}
                    />

                    <SalesPublicApiKeyBlock
                      tr={(k) => tr(k)}
                      apiKey={orgApiKeys[org.id] ?? null}
                      loading={!!orgApiKeyLoading[org.id]}
                      error={!!orgApiKeyError[org.id]}
                      compact
                      regenerating={regeneratingOrgId === org.id}
                      onRegenerate={() => handleRegenerateApiKey(org.id)}
                    />

                    {/* Module Access */}
                    <div>
                      <Text variant="label" className="mb-3">{tr('settingsOrgsModuleAccess')}</Text>
                      <div className="space-y-2">
                        {registry.map((mod) => {
                          const enabled = isModuleEnabled(mod.id);
                          return (
                            <div key={mod.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-surface-page border border-subtle">
                              <div className="flex items-center gap-3">
                                <ModuleBadges modules={[mod.id]} size="md" />
                                <div>
                                  <span className="text-sm text-content">{mod.name}</span>
                                  <span className="text-[11px] text-content-muted block">{mod.roles.length !== 1 ? tr('settingsOrgsRoleCountPlural', { count: mod.roles.length }) : tr('settingsOrgsRoleCount', { count: mod.roles.length })}</span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleToggleModule(org.id, mod.id, enabled)}
                                className={`relative w-10 h-5 rounded-full border-none cursor-pointer transition-colors ${enabled ? 'bg-status-success' : 'bg-surface-primary border border-default'}`}
                              >
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${enabled ? 'left-5' : 'left-0.5'}`} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Provision prompt modal */}
      {provisionPrompt && (
        <Modal isOpen onClose={() => setProvisionPrompt(null)}>
          <Modal.Header title={tr('settingsProvisionModalTitle')} icon={<AlertTriangle size={20} className="text-amber-400" />} onClose={() => setProvisionPrompt(null)} />
          <Modal.Body>
            <Text variant="body" className="text-content-secondary">
              {tr('settingsProvisionModalBody')}
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onClick={() => setProvisionPrompt(null)}>{tr('settingsProvisionBtnCancel')}</Button>
            <Button variant="secondary" onClick={() => handleProvisionConfirm('none')}>{tr('settingsProvisionBtnNoneManual')}</Button>
            <Button variant="primary" onClick={() => handleProvisionConfirm('all')}>{tr('settingsProvisionBtnAllUsers')}</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Invite user modal */}
      {inviteTarget && (
        <InviteUserModal
          isOpen
          onClose={() => setInviteTarget(null)}
          users={inviteTarget.users}
          departments={inviteTarget.departments}
          orgEnabledModules={inviteTarget.enabledModules}
          moduleRegistry={registry}
          onInvite={(data) => platformInviteUser(inviteTarget.org.id, data)}
        />
      )}
    </div>
  );
};

// ── Demo Tab ──

const DemoPanel: React.FC = () => {
  const { language } = useUIStore();
  const tr = (key: string, vars?: Record<string, string | number>) => getTranslation(language, key, vars);
  const { success, error: showError } = useToast();
  const [orgName, setOrgName] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [lastSeedResult, setLastSeedResult] = useState<DemoSeedResponse | null>(null);
  const [demoOrgs, setDemoOrgs] = useState<DemoOrgListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<DemoOrgListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const copy = useCallback((text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const loadDemoOrgs = useCallback(async () => {
    setListLoading(true);
    try { setDemoOrgs(await listDemoOrgs()); } catch { /* empty */ }
    finally { setListLoading(false); }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadDemoOrgs();
  }, [loadDemoOrgs]);

  const handleSeed = async () => {
    if (!orgName.trim()) return;
    setSeeding(true);
    try {
      const result = await seedDemoOrg({ org_name: orgName.trim() });
      setLastSeedResult(result);
      setOrgName('');
      success(tr('settingsDemoToastDemoOrgCreated'));
      loadDemoOrgs();
    } catch (e: any) {
      showError(e?.response?.data?.detail || tr('settingsDemoErrorFailedToCreate'));
    } finally {
      setSeeding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDemoOrg(deleteTarget.org_id);
      setDeleteTarget(null);
      success(tr('settingsDemoToastDemoOrgDeleted'));
      loadDemoOrgs();
    } catch (e: any) {
      showError(e?.response?.data?.detail || tr('settingsDemoErrorFailedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  const LOCALE_MAP: Record<string, string> = { en: 'en-US', es: 'es-ES', pt: 'pt-BR' };
  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(LOCALE_MAP[language] || 'en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4">
      <Text variant="subheading">{tr('settingsDemoTitle')}</Text>
      <Text variant="body" className="text-content-muted">
        {tr('settingsDemoSubtitle')}
      </Text>

      {lastSeedResult && (
        <div className="p-5 rounded-2xl bg-status-success/[0.06] border border-status-success/20 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <Text variant="label" className="text-status-success flex items-center gap-2">
              <Check size={16} /> {tr('settingsDemoCreatedTitle')}
            </Text>
            <button onClick={() => setLastSeedResult(null)} className="text-xs text-content-muted hover:text-content-tertiary border-none outline-none cursor-pointer">{tr('settingsDemoDismiss')}</button>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Text variant="label">{tr('settingsDemoFieldOrganization')}</Text>
              <p className="text-sm text-content mt-1">{lastSeedResult.org_name}</p>
            </div>
            <div>
              <Text variant="label">{tr('settingsDemoFieldStats')}</Text>
              <p className="text-sm text-content-secondary mt-1">{tr('settingsDemoStatUsers', { count: lastSeedResult.user_count })}, {tr('settingsDemoStatKpis', { count: lastSeedResult.kpi_count })}</p>
            </div>
          </div>
          <div className="space-y-2 p-3 rounded-xl bg-black/20 border border-subtle">
            <Text variant="label" className="mb-2">{tr('settingsDemoFieldCredentials')}</Text>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[11px] text-content-muted">{tr('settingsDemoFieldEmail')}</span>
                <p className="text-sm text-content font-mono">{lastSeedResult.demo_email}</p>
              </div>
              <button onClick={() => copy(lastSeedResult.demo_email, 'email')} className="p-1.5 rounded-lg hover:bg-surface-primary text-content-muted border-none outline-none cursor-pointer">
                {copiedField === 'email' ? <Check size={14} className="text-status-success" /> : <Copy size={14} />}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[11px] text-content-muted">{tr('settingsDemoFieldPassword')}</span>
                <p className="text-sm text-content font-mono">{lastSeedResult.demo_password}</p>
              </div>
              <button onClick={() => copy(lastSeedResult.demo_password, 'pw')} className="p-1.5 rounded-lg hover:bg-surface-primary text-content-muted border-none outline-none cursor-pointer">
                {copiedField === 'pw' ? <Check size={14} className="text-status-success" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <input
          type="text"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSeed()}
          placeholder={tr('settingsDemoPlaceholderOrgName')}
          disabled={seeding}
          className="flex-1 px-4 py-2.5 rounded-xl bg-surface-primary border border-subtle text-content text-xs placeholder-content-muted outline-none focus:border-brand/30 transition-all disabled:opacity-50"
        />
        <Button variant="success" size="md" loading={seeding} disabled={!orgName.trim()} leftIcon={<Plus size={14} />} onClick={handleSeed}>
          {tr('settingsDemoBtnCreateDemoOrg')}
        </Button>
      </div>

      {listLoading ? (
        <div className="flex items-center justify-center py-8"><Spinner /></div>
      ) : demoOrgs.length === 0 ? (
        <div className="text-center py-8">
          <Globe size={24} className="text-content-faint mx-auto mb-2" />
          <Text variant="body" className="text-content-muted">{tr('settingsDemoNoDemoOrgs')}</Text>
        </div>
      ) : (
        <div className="space-y-2">
          <Text variant="label">{demoOrgs.length !== 1 ? tr('settingsDemoOrgCountPlural', { count: demoOrgs.length }) : tr('settingsDemoOrgCount', { count: demoOrgs.length })}</Text>
          {demoOrgs.map((org) => (
            <div key={org.org_id} className="flex items-center gap-4 p-3 rounded-xl bg-surface-primary border border-subtle hover:border-default transition-all">
              <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <Globe size={18} className="text-brand/60" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-content block truncate">{org.org_name}</span>
                <div className="flex items-center gap-3 mt-1">
                  <span className="inline-flex items-center gap-1 text-[11px] text-content-tertiary font-mono">
                    {org.demo_email}
                    <button onClick={(e) => { e.stopPropagation(); copy(org.demo_email, `${org.org_id}-email`); }} className="w-5 h-5 inline-flex items-center justify-center rounded bg-surface-primary hover:bg-surface-elevated text-content-muted border-none outline-none cursor-pointer ml-0.5">
                      {copiedField === `${org.org_id}-email` ? <Check size={10} className="text-status-success" /> : <Copy size={10} />}
                    </button>
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-content-muted"><Users size={10} /> {org.user_count}</span>
                </div>
              </div>
              <span className="text-[11px] text-content-muted shrink-0 hidden sm:block">{formatDate(org.created_at)}</span>
              <button onClick={() => setDeleteTarget(org)} className="p-2 rounded-lg text-content-faint hover:text-status-error hover:bg-status-error/10 transition-colors border-none outline-none cursor-pointer shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <Modal.Header title={tr('settingsDemoDeleteTitle')} icon={<AlertTriangle size={20} className="text-status-error" />} onClose={() => setDeleteTarget(null)} />
        <Modal.Body>
          <Text variant="body" className="text-content-secondary">
            {tr('settingsDemoDeleteBody', { name: deleteTarget?.org_name ?? '' })}
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>{tr('settingsDemoBtnCancel')}</Button>
          <Button variant="danger" loading={deleting} onClick={handleDelete}>{tr('settingsDemoBtnDeleteEverything')}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// ── AI / LLM Tab ──

const AiPanel: React.FC = () => {
  const { language } = useUIStore();
  const tr = (key: string, vars?: Record<string, string | number>) => getTranslation(language, key, vars);
  const { success, error } = useToast();
  const [status, setStatus] = useState<LlmApiKeyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [model, setModel] = useState('');
  const [modelDraft, setModelDraft] = useState('');
  const [modelLoading, setModelLoading] = useState(true);
  const [modelSaving, setModelSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setStatus(await getPlatformLlmApiKey());
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadModel = useCallback(async () => {
    setModelLoading(true);
    try {
      const r = await getPlatformLlmModel();
      setModel(r.model);
      setModelDraft(r.model);
    } catch {
      /* keep empty draft */
    } finally {
      setModelLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadModel();
  }, [load, loadModel]);

  const handleSave = useCallback(async (apiKey: string) => {
    setSaving(true);
    try {
      setStatus(await setPlatformLlmApiKey(apiKey));
      success(tr('settingsLlmSaveSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmSaveError');
      error(String(msg));
      throw e;
    } finally {
      setSaving(false);
    }
  }, [error, success, tr]);

  const handleClear = useCallback(async () => {
    setClearing(true);
    try {
      setStatus(await clearPlatformLlmApiKey());
      success(tr('settingsLlmClearSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmClearError');
      error(String(msg));
      throw e;
    } finally {
      setClearing(false);
    }
  }, [error, success, tr]);

  const handleSaveModel = useCallback(async () => {
    const next = modelDraft.trim();
    if (!next) return;
    setModelSaving(true);
    try {
      const r = await setPlatformLlmModel(next);
      setModel(r.model);
      setModelDraft(r.model);
      success(tr('settingsLlmModelSaveSuccess'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        tr('settingsLlmModelSaveError');
      error(String(msg));
    } finally {
      setModelSaving(false);
    }
  }, [error, modelDraft, success, tr]);

  return (
    <div className="space-y-4">
      <LlmApiKeyBlock
        tr={tr}
        status={status}
        loading={loading}
        error={loadError}
        saving={saving}
        clearing={clearing}
        onSave={handleSave}
        onClear={handleClear}
      />

      <div className="p-5 rounded-2xl bg-surface-primary border border-subtle">
        <h3 className="text-sm font-semibold text-content mb-1">{tr('settingsLlmModelTitle')}</h3>
        <p className="text-[11px] text-content-muted mb-4">{tr('settingsLlmModelHelp')}</p>
        {modelLoading ? (
          <Spinner size="sm" />
        ) : (
          <div className="space-y-3">
            <TextField
              label={tr('settingsLlmModelFieldLabel')}
              value={modelDraft}
              onChange={(e) => setModelDraft(e.target.value)}
              placeholder={tr('settingsLlmModelFieldPlaceholder')}
              autoComplete="off"
            />
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={modelSaving}
              disabled={modelSaving || !modelDraft.trim() || modelDraft.trim() === model}
              onClick={() => void handleSaveModel()}
            >
              {tr('settingsLlmModelBtnSave')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ──

const PlatformSettingsView: React.FC = () => {
  const [tab, setTab] = useState<PlatformTab>('tenants');
  const { language } = useUIStore();
  const tr = (key: string) => getTranslation(language, key);

  const TAB_ITEMS = useMemo(() => [
    { id: 'tenants' as PlatformTab, label: tr('settingsTabTenants') },
    { id: 'organizations' as PlatformTab, label: tr('settingsTabOrganizations') },
    { id: 'ai' as PlatformTab, label: tr('settingsTabAi') },
    { id: 'demo' as PlatformTab, label: tr('settingsTabDemo') },
  ], [language]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex-1 flex flex-col animate-fade-in min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-subtle">
        <div>
          <h1 className="text-lg font-bold text-content">{tr('settingsPlatformTitle')}</h1>
          <p className="text-xs text-content-muted mt-0.5">{tr('settingsPlatformSubtitle')}</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 px-6 pt-3 pb-0 border-b border-subtle">
        {TAB_ITEMS.map((tabItem) => (
          <button
            key={tabItem.id}
            onClick={() => setTab(tabItem.id)}
            className={`px-4 py-2 text-xs font-medium rounded-t-lg border-none outline-none cursor-pointer transition-colors ${
              tab === tabItem.id
                ? 'bg-surface-primary text-status-success border-b-2 border-status-success'
                : 'text-content-muted hover:text-content-secondary hover:bg-surface-primary/50'
            }`}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        <div className="max-w-3xl">
          {tab === 'tenants' && <TenantsPanel />}
          {tab === 'organizations' && <OrgsPanel />}
          {tab === 'ai' && <AiPanel />}
          {tab === 'demo' && <DemoPanel />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(PlatformSettingsView);
